import type { AdminAttentionDto } from '@ecom/contracts';
import type { MessageKey } from '@/i18n/messages';

/**
 * The admin's navigation, as data.
 *
 * Two halves, the split an operator already has in mind (ADR-023): the daily
 * work — orders, products, customers — down the sidebar, and the setup behind
 * one **Settings** entry at its foot, which opens an area of its own with its
 * own menu. The sidebar stays a handful of rows however many screens the admin
 * grows, and nobody hunting for an order has to scroll past webhooks.
 *
 * Each sidebar section is a page first: "Products" opens the product list, and
 * the pages inside it (categories, looks, inventory…) unfold beneath it while
 * one of them is open. The shell draws it; this module decides what exists,
 * who sees it, and where a page lives.
 */

export type NavIcon =
  | 'archive'
  | 'bolt'
  | 'box'
  | 'briefcase'
  | 'cart'
  | 'chart'
  | 'chat'
  | 'clock'
  | 'code'
  | 'cog'
  | 'coins'
  | 'file'
  | 'grid'
  | 'home'
  | 'key'
  | 'layers'
  | 'layout'
  | 'mail'
  | 'megaphone'
  | 'menu'
  | 'palette'
  | 'percent'
  | 'plug'
  | 'question'
  | 'receipt'
  | 'return'
  | 'shield'
  | 'sliders'
  | 'sparkle'
  | 'star'
  | 'store'
  | 'swatch'
  | 'tag'
  | 'truck'
  | 'upload'
  | 'users'
  | 'wallet'
  | 'gift';

/** A count from `GET /admin/attention` a menu entry wears as its badge. */
export type AttentionKey = keyof AdminAttentionDto;

export interface NavItem {
  to: string;
  key: MessageKey;
  icon: NavIcon;
  /** Owner-only areas are hidden from staff; the API enforces it regardless. */
  ownerOnly?: boolean;
  /**
   * The permission the page's own list needs. Staff without it are not shown
   * a page that would only answer 403 — the API refuses regardless.
   */
  permission?: string;
  /** What is waiting there, as a count beside the entry. */
  badge?: AttentionKey;
}

/** Who is looking at the menu: an owner sees everything; staff what their permissions open. */
export interface Viewer {
  isOwner: boolean;
  permissions: readonly string[];
}

export type NavSectionId = 'orders' | 'catalog' | 'customers' | 'care' | 'marketing' | 'storefront' | 'analytics';

export interface NavSection {
  id: NavSectionId;
  key: MessageKey;
  icon: NavIcon;
  /** The first visible item is where the section's own entry goes. */
  items: NavItem[];
}

export const HOME: NavItem = { to: '/overview', key: 'overview', icon: 'home' };

export const SECTIONS: NavSection[] = [
  {
    id: 'orders',
    key: 'orders',
    icon: 'cart',
    items: [
      { to: '/orders', key: 'orders', icon: 'cart', permission: 'orders:read', badge: 'ordersToFulfil' },
      { to: '/returns', key: 'returns', icon: 'return', permission: 'returns:read', badge: 'returnsToHandle' },
      // Every invoice and credit note, for the accountant (ADR-067).
      { to: '/invoices', key: 'invoices', icon: 'receipt', permission: 'orders:read' },
    ],
  },
  {
    id: 'catalog',
    key: 'products',
    icon: 'box',
    items: [
      { to: '/products', key: 'products', icon: 'box', permission: 'products:read' },
      { to: '/categories', key: 'categories', icon: 'layers', permission: 'products:read' },
      { to: '/brands', key: 'brands', icon: 'tag', permission: 'products:read' },
      { to: '/looks', key: 'looks', icon: 'sparkle', permission: 'products:read' },
      { to: '/options', key: 'optionsLibrary', icon: 'swatch', permission: 'products:read' },
      // Tables written once and hung on categories and products (ADR-072).
      { to: '/size-charts', key: 'sizeCharts', icon: 'sliders', permission: 'products:read' },
      { to: '/inventory', key: 'inventory', icon: 'archive', permission: 'inventory:manage' },
      { to: '/attributes', key: 'attributes', icon: 'sliders', ownerOnly: true },
      { to: '/import', key: 'import', icon: 'upload', permission: 'import:run' },
    ],
  },
  {
    id: 'customers',
    key: 'customers',
    icon: 'users',
    items: [
      { to: '/customers', key: 'customers', icon: 'users', permission: 'customers:read' },
      // What must happen next, by whom and when (ADR-104); the badge counts mine that are due.
      { to: '/follow-ups', key: 'followUps', icon: 'clock', permission: 'customers:read', badge: 'followUpsDue' },
      // Groups by rules, kept and refreshed nightly (ADR-085).
      { to: '/customer-groups', key: 'customerGroups', icon: 'layers', permission: 'customers:read' },
      // What shoppers are holding: carts, wishlists and compare lists (ADR-063).
      { to: '/carts', key: 'shopperActivity', icon: 'cart', permission: 'customers:read' },
      { to: '/trade-applications', key: 'tradeApplications', icon: 'briefcase', permission: 'customers:read', badge: 'tradeApplicationsPending' },
      { to: '/price-lists', key: 'priceLists', icon: 'coins', permission: 'discounts:manage' },
    ],
  },
  {
    id: 'care',
    key: 'navCare',
    icon: 'chat',
    items: [
      { to: '/support', key: 'supportTickets', icon: 'chat', permission: 'customers:read', badge: 'ticketsOpen' },
      { to: '/reviews', key: 'reviews', icon: 'star', permission: 'reviews:moderate', badge: 'reviewsPending' },
      { to: '/questions', key: 'questions', icon: 'question', permission: 'reviews:moderate', badge: 'questionsPending' },
    ],
  },
  {
    id: 'marketing',
    key: 'navMarketing',
    icon: 'megaphone',
    items: [
      { to: '/discounts', key: 'discounts', icon: 'percent', permission: 'discounts:manage' },
      { to: '/promotions', key: 'promotions', icon: 'tag', ownerOnly: true },
      { to: '/campaigns', key: 'campaigns', icon: 'megaphone', ownerOnly: true },
      // Emails that send themselves, and who signed up for the newsletter (ADR-099).
      { to: '/automations', key: 'automationsPage', icon: 'bolt', ownerOnly: true },
      { to: '/subscribers', key: 'subscribersPage', icon: 'mail', permission: 'customers:read' },
      // Popups and the bar above every page (ADR-098).
      { to: '/popups', key: 'popupsPage', icon: 'sparkle', permission: 'discounts:manage' },
      // The store's blog (ADR-080): marketing in the store's own words, written by whoever manages content.
      { to: '/blog', key: 'blog', icon: 'file', permission: 'content:manage' },
      // What search engines would find missing across the catalogue (ADR-095).
      { to: '/seo', key: 'seoHealthPage', icon: 'chart', permission: 'products:read' },
      // The catalogue as Google, Meta, TikTok and Snapchat read it (ADR-096).
      { to: '/feeds', key: 'feedsPage', icon: 'layers', permission: 'products:read' },
      // Store links tagged for where they are posted, and what they made (ADR-097).
      { to: '/campaign-links', key: 'campaignLinks', icon: 'plug', permission: 'dashboard:read' },
      // Gift cards (ADR-082): money given away, so whoever may refund.
      { to: '/gift-cards', key: 'giftCards', icon: 'gift', permission: 'refunds:manage' },
      // Affiliates (ADR-084): marketers paid for the orders they bring.
      { to: '/affiliates', key: 'affiliates', icon: 'users', permission: 'discounts:manage' },
    ],
  },
  {
    id: 'storefront',
    key: 'navOnlineStore',
    icon: 'store',
    items: [
      { to: '/branding', key: 'branding', icon: 'palette', ownerOnly: true },
      { to: '/storefront/home', key: 'homeLayout', icon: 'layout', ownerOnly: true },
      { to: '/navigation', key: 'navigationMenu', icon: 'menu', ownerOnly: true },
      { to: '/storefront/pages', key: 'contentPages', icon: 'file', ownerOnly: true },
      // Old links that keep working (ADR-073).
      { to: '/redirects', key: 'redirects', icon: 'menu', ownerOnly: true },
    ],
  },
  {
    id: 'analytics',
    key: 'navAnalytics',
    icon: 'chart',
    items: [{ to: '/reports', key: 'reports', icon: 'chart', permission: 'dashboard:read' }],
  },
];

// ─── Settings ────────────────────────────────────────────────────────────────

/** The sidebar's last entry: the whole settings area. */
export const SETTINGS: NavItem = { to: '/settings', key: 'settings', icon: 'cog' };

export interface SettingsItem extends NavItem {
  /** One line on the settings home page: what is behind it. */
  hint: MessageKey;
}

export type SettingsGroupId = 'store' | 'selling' | 'communication' | 'apps' | 'team';

export interface SettingsGroup {
  id: SettingsGroupId;
  key: MessageKey;
  items: SettingsItem[];
}

/**
 * The settings area's own menu, grouped the way the reference dashboards group
 * theirs: the store itself, how it sells, how it talks, what it plugs into,
 * and who works in it.
 */
export const SETTINGS_GROUPS: SettingsGroup[] = [
  {
    id: 'store',
    key: 'settingsGroupStore',
    items: [
      { to: '/settings/general', key: 'navGeneral', icon: 'store', hint: 'settingsGeneralHint', ownerOnly: true },
      { to: '/settings/checkout', key: 'settingsCheckout', icon: 'receipt', hint: 'settingsCheckoutHint', ownerOnly: true },
      { to: '/settings/customers', key: 'settingsCustomers', icon: 'users', hint: 'settingsCustomersHint', ownerOnly: true },
      { to: '/settings/catalog', key: 'settingsCatalog', icon: 'box', hint: 'settingsCatalogHint', ownerOnly: true },
      { to: '/settings/seo', key: 'settingsSeo', icon: 'chart', hint: 'settingsSeoHint', ownerOnly: true },
      { to: '/settings/features', key: 'featureFlags', icon: 'sliders', hint: 'settingsFeaturesHint', ownerOnly: true },
    ],
  },
  {
    id: 'selling',
    key: 'settingsGroupSelling',
    items: [
      { to: '/settings/payments', key: 'paymentProviders', icon: 'wallet', hint: 'settingsPaymentsHint', ownerOnly: true },
      { to: '/settings/shipping', key: 'settingsShipping', icon: 'truck', hint: 'settingsShippingHint', ownerOnly: true },
      { to: '/settings/locations', key: 'settingsLocations', icon: 'archive', hint: 'settingsLocationsHint', ownerOnly: true },
      { to: '/settings/taxes', key: 'settingsTaxes', icon: 'percent', hint: 'settingsTaxesHint', ownerOnly: true },
    ],
  },
  {
    id: 'communication',
    key: 'settingsGroupCommunication',
    items: [
      { to: '/settings/notifications', key: 'settingsNotifications', icon: 'mail', hint: 'settingsNotificationsHint', ownerOnly: true },
      { to: '/settings/messaging', key: 'messaging', icon: 'chat', hint: 'settingsMessagingHint', ownerOnly: true },
      // Order updates on WhatsApp through Meta (ADR-105); the keys stay in Integrations.
      { to: '/settings/whatsapp', key: 'whatsapp', icon: 'chat', hint: 'whatsappHint', permission: 'settings:manage' },
      { to: '/settings/messages', key: 'messageLog', icon: 'receipt', hint: 'settingsMessageLogHint', permission: 'customers:read', badge: 'messagesFailed' },
    ],
  },
  {
    id: 'apps',
    key: 'settingsGroupApps',
    items: [
      { to: '/settings/integrations', key: 'integrations', icon: 'plug', hint: 'settingsIntegrationsHint', ownerOnly: true },
      { to: '/settings/ai', key: 'aiAssistant', icon: 'sparkle', hint: 'settingsAiHint', ownerOnly: true },
      { to: '/settings/api-keys', key: 'apiKeys', icon: 'key', hint: 'settingsApiKeysHint', ownerOnly: true },
      { to: '/settings/webhooks', key: 'webhooks', icon: 'bolt', hint: 'settingsWebhooksHint', ownerOnly: true },
      { to: '/settings/custom-code', key: 'settingsCustomCode', icon: 'code', hint: 'settingsCustomCodeHint', ownerOnly: true },
    ],
  },
  {
    id: 'team',
    key: 'settingsGroupTeam',
    items: [
      { to: '/settings/team', key: 'team', icon: 'shield', hint: 'settingsTeamHint', ownerOnly: true },
      { to: '/settings/audit', key: 'auditLog', icon: 'clock', hint: 'settingsAuditHint', ownerOnly: true },
      { to: '/settings/advanced', key: 'advanced', icon: 'code', hint: 'settingsAdvancedHint', ownerOnly: true },
    ],
  },
];

/**
 * Where the settings pages used to live, before they had an area of their own.
 * Old bookmarks land on the page's new address rather than on "not found".
 */
export const MOVED: Record<string, string> = {
  '/payments': '/settings/payments',
  '/shipping': '/settings/shipping',
  '/email-templates': '/settings/notifications',
  '/messaging': '/settings/messaging',
  '/messages': '/settings/messages',
  '/ai': '/settings/ai',
  '/integrations': '/settings/integrations',
  '/api-keys': '/settings/api-keys',
  '/webhooks': '/settings/webhooks',
  '/team': '/settings/team',
  '/audit': '/settings/audit',
};

// ─── Who sees what ───────────────────────────────────────────────────────────

/** Whether this person may open the page: an owner always; staff when it is not the owner's and they hold what it needs. */
export function mayOpen(item: NavItem, viewer: Viewer): boolean {
  if (viewer.isOwner) return true;
  if (item.ownerOnly) return false;
  return !item.permission || viewer.permissions.includes(item.permission);
}

/** The sections someone may use: pages they may not open dropped, and a section left empty dropped with them. */
export function visibleSections(viewer: Viewer, sections: NavSection[] = SECTIONS): NavSection[] {
  return sections
    .map((section) => ({ ...section, items: section.items.filter((item) => mayOpen(item, viewer)) }))
    .filter((section) => section.items.length > 0);
}

/** The settings groups someone may use, on the same rule. */
export function visibleSettings(viewer: Viewer, groups: SettingsGroup[] = SETTINGS_GROUPS): SettingsGroup[] {
  return groups
    .map((group) => ({ ...group, items: group.items.filter((item) => mayOpen(item, viewer)) }))
    .filter((group) => group.items.length > 0);
}

/** Whether `pathname` is the page at `to`, or one inside it — `/orders/123` is in Orders. */
export function isWithin(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`);
}

/** The section holding the page at `pathname`, or null: Overview, Settings, or a page the menu does not list. */
export function sectionOf(pathname: string, sections: NavSection[]): NavSectionId | null {
  return sections.find((section) => section.items.some((item) => isWithin(pathname, item.to)))?.id ?? null;
}

/**
 * Where a page sits, for the top bar: its section, then the page — or the page
 * alone when it is its section's own first page, or Home. Settings pages sit
 * under Settings. A detail page (`/orders/123`) shows its list's trail; the page
 * header names the record.
 */
export function crumbsOf(pathname: string, sections: NavSection[]): NavItem[] {
  if (isWithin(pathname, HOME.to) || pathname === '/') return [HOME];

  if (isWithin(pathname, SETTINGS.to)) {
    const item = SETTINGS_GROUPS.flatMap((group) => group.items).find((entry) => isWithin(pathname, entry.to));
    return item ? [SETTINGS, item] : [SETTINGS];
  }

  for (const section of sections) {
    // The longest match wins, so `/orders/returns` style paths pick the deeper page.
    const item = section.items
      .filter((entry) => isWithin(pathname, entry.to))
      .sort((a, b) => b.to.length - a.to.length)[0];
    if (!item) continue;
    const first = section.items[0]!;
    return item === first && item.key === section.key ? [item] : [{ ...first, key: section.key }, item];
  }

  return [];
}

/** Every page someone may open from the menu, for the page search. */
export function searchablePages(viewer: Viewer): { item: NavItem; group: MessageKey | null }[] {
  return [
    { item: HOME, group: null },
    ...visibleSections(viewer).flatMap((section) => section.items.map((item) => ({ item, group: section.key }))),
    { item: SETTINGS, group: null },
    ...visibleSettings(viewer).flatMap((group) => group.items.map((item) => ({ item, group: group.key }))),
  ];
}

/** The viewer a signed-in person is. */
export function viewerOf(user: { role: string; permissions: readonly string[] } | null): Viewer {
  return { isOwner: user?.role === 'OWNER', permissions: user?.permissions ?? [] };
}
